/**
 * Leaderboard Podium
 * Displays top 3 users in a classic podium layout (2nd - 1st - 3rd).
 * Wider and more detailed than the mobile version, taking advantage
 * of the extra horizontal space available on web.
 */

import { PrivateValue } from '@components/ui/PrivateValue';
import { UserProfileModal } from '@components/ui/UserProfileModal';
import type { LeaderboardEntry, SortBy } from '@parlay-picker/shared/hooks';
import { formatPodiumStat } from '@parlay-picker/shared/leaderboardDisplay';
import { Award, Crown, Medal } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Config
// =============================================================================

const PODIUM_CONFIG = [
  {
    rank: 1,
    Icon: Crown,
    label: '1st',
    color: '#FFD700',
    bgGlow: 'from-yellow-500/20 to-yellow-600/5',
    border: 'border-yellow-500/30',
  },
  {
    rank: 2,
    Icon: Medal,
    label: '2nd',
    color: '#94A3B8',
    bgGlow: 'from-slate-400/15 to-slate-500/5',
    border: 'border-slate-400/25',
  },
  {
    rank: 3,
    Icon: Award,
    label: '3rd',
    color: '#CD7F32',
    bgGlow: 'from-orange-500/15 to-orange-600/5',
    border: 'border-orange-500/25',
  },
] as const;

// =============================================================================
// Helpers
// =============================================================================

function getStatLabel(sortBy: SortBy): string {
  switch (sortBy) {
    case 'roi':
    case 'rawRoi':
      return 'ROI';
    case 'profit':
      return 'Profit';
    case 'streak':
      return 'Streak';
    default:
      return 'Win Rate';
  }
}

// =============================================================================
// Types
// =============================================================================

interface LeaderboardPodiumProps {
  entries: LeaderboardEntry[];
  sortBy: SortBy;
}

// =============================================================================
// Component
// =============================================================================

export function LeaderboardPodium({
  entries,
  sortBy,
}: LeaderboardPodiumProps): ReactElement | null {
  const top3 = entries.slice(0, 3);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  if (top3.length < 3) return null;

  // Reorder to 2nd - 1st - 3rd for classic podium layout
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const configOrder = [PODIUM_CONFIG[1], PODIUM_CONFIG[0], PODIUM_CONFIG[2]];

  const handleUserClick = (entry: LeaderboardEntry) => setSelectedUserId(entry.user.id);

  return (
    <>
      {selectedUserId && (
        <UserProfileModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}

      <div
        data-testid="leaderboard-podium"
        className="bg-theme-surface/50 border border-theme-border rounded-lg p-6 md:p-8"
      >
        <div className="flex items-end justify-center gap-3 md:gap-6 max-w-2xl mx-auto">
          {podiumOrder.map((entry, i) => {
            const config = configOrder[i];
            const isFirst = config.rank === 1;
            const primaryStat = formatPodiumStat(entry.stats, sortBy);
            const pedestalHeight = isFirst
              ? 'h-28 md:h-36'
              : config.rank === 2
                ? 'h-20 md:h-28'
                : 'h-16 md:h-24';

            return (
              <button
                key={entry.user.id}
                type="button"
                onClick={() => handleUserClick(entry)}
                className="flex-1 flex flex-col items-center group cursor-pointer min-w-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                {/* Medal icon with glow ring */}
                <div
                  className={`flex items-center justify-center rounded-full mb-2 md:mb-3 transition-transform group-hover:scale-110 ${
                    isFirst ? 'w-14 h-14 md:w-16 md:h-16' : 'w-10 h-10 md:w-12 md:h-12'
                  }`}
                  style={{
                    border: `2px solid ${config.color}`,
                    backgroundColor: `${config.color}15`,
                    boxShadow: `0 0 20px ${config.color}30`,
                  }}
                >
                  <config.Icon size={isFirst ? 24 : 18} color={config.color} />
                </div>

                {/* Name */}
                <p
                  className={`font-bold text-theme-text truncate max-w-full mb-1 ${
                    isFirst ? 'text-sm md:text-base' : 'text-xs md:text-sm'
                  }`}
                >
                  {entry.user.displayName || 'Anonymous'}
                </p>

                {/* Pedestal */}
                <div
                  className={`w-full rounded-t-xl border border-b-0 flex flex-col items-center justify-center gap-1 ${pedestalHeight} ${config.border} transition-colors group-hover:brightness-125`}
                  style={{
                    background: `linear-gradient(to top, ${config.color}18, ${config.color}08)`,
                  }}
                >
                  {/* Rank label */}
                  <span
                    className={`font-extrabold font-mono ${isFirst ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'}`}
                    style={{ color: config.color }}
                  >
                    {config.label}
                  </span>

                  {/* Primary stat */}
                  {primaryStat === null ? (
                    <PrivateValue />
                  ) : (
                    <span className="text-sm md:text-base font-bold text-theme-success">
                      {primaryStat}
                    </span>
                  )}

                  {/* Stat label */}
                  <span className="text-[10px] @3xl:text-xs text-theme-text-tertiary hidden @3xl:block">
                    {getStatLabel(sortBy)}
                  </span>

                  {/* Record */}
                  <span className="text-[10px] md:text-xs text-theme-text-tertiary">
                    {entry.stats.wins}W-{entry.stats.losses}L
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
